import type { Room, Corridor, RoomStyle, Point, HexLayout } from "../types.ts";
import { hexNeighbors, keyToHex, hexToKey, hexToPixel } from "../hex/coordinates.ts";

/**
 * Generate a unique ID for rooms and corridors.
 * Uses crypto.randomUUID() for uniqueness.
 */
export function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Pastel color palette for adjacent rooms
 * Colors are chosen to be visually distinct when adjacent
 */
const PASTEL_PALETTE = [
  "#f4ece0", // Default warm cream (index 0 - isolated rooms)
  "#d4e8d4", // Soft green (more saturated)
  "#e0d4e8", // Soft purple/lavender
  "#d4e0e8", // Soft sky blue
  "#f0d4d4", // Soft coral/pink
  "#e8e0d0", // Soft tan/sand
  "#d4e8e0", // Soft teal/mint
  "#e8d4e0", // Soft mauve
];

/**
 * Create a default room style based on whether it was user-drawn.
 * Uses warm parchment tones.
 */
export function createDefaultRoomStyle(userDrawn: boolean): RoomStyle {
  return {
    fill: "#f4ece0", // Warm cream/parchment
    stroke: userDrawn ? "#5c4a3d" : "#7a6a5a", // Warm brown tones
  };
}

/**
 * DungeonModel manages the complete dungeon state including rooms and corridors.
 * Provides methods for adding, removing, and querying dungeon elements.
 */
export class DungeonModel {
  rooms: Map<string, Room>;
  corridors: Map<string, Corridor>;
  private hexToRoom: Map<string, string>;

  constructor() {
    this.rooms = new Map();
    this.corridors = new Map();
    this.hexToRoom = new Map();
  }

  /**
   * Get the room containing a specific hex coordinate.
   * @param hexKey - String key representing the hex (e.g., "0,0")
   * @returns The room containing this hex, or undefined if not found
   */
  getRoom(hexKey: string): Room | undefined {
    const roomId = this.hexToRoom.get(hexKey);
    if (!roomId) return undefined;
    return this.rooms.get(roomId);
  }

  /**
   * Get a room by its ID.
   * @param roomId - The unique room identifier
   * @returns The room, or undefined if not found
   */
  getRoomById(roomId: string): Room | undefined {
    return this.rooms.get(roomId);
  }

  /**
   * Get all corridors connected to a specific room.
   * @param roomId - The room ID to find connections for
   * @returns Array of corridors that connect to this room
   */
  getRoomConnections(roomId: string): Corridor[] {
    const connections: Corridor[] = [];
    for (const corridor of this.corridors.values()) {
      if (corridor.connects[0] === roomId || corridor.connects[1] === roomId) {
        connections.push(corridor);
      }
    }
    return connections;
  }

  /**
   * Create and add a new room to the dungeon.
   * @param hexes - Set of hex keys that make up this room
   * @param label - Optional label for the room (defaults to generated ID)
   * @param userDrawn - Whether this room was user-drawn (defaults to false)
   * @returns The newly created room
   */
  addRoom(
    hexes: Set<string>,
    label?: string,
    userDrawn: boolean = false
  ): Room {
    const id = generateId();
    const room: Room = {
      id,
      hexes,
      label: label ?? id,
      userDrawn,
      style: createDefaultRoomStyle(userDrawn),
    };

    this.rooms.set(id, room);

    // Update hex-to-room mapping for fast lookup
    for (const hexKey of hexes) {
      this.hexToRoom.set(hexKey, id);
    }

    return room;
  }

  /**
   * Remove a room from the dungeon.
   * Also removes any corridors connected to this room.
   * @param roomId - The ID of the room to remove
   */
  removeRoom(roomId: string): void {
    const room = this.rooms.get(roomId);
    if (!room) return;

    // Remove hex-to-room mappings
    for (const hexKey of room.hexes) {
      this.hexToRoom.delete(hexKey);
    }

    // Remove the room
    this.rooms.delete(roomId);

    // Remove any corridors connected to this room
    const connectedCorridors = this.getRoomConnections(roomId);
    for (const corridor of connectedCorridors) {
      this.corridors.delete(corridor.id);
    }
  }

  /**
   * Create a corridor between two rooms.
   * @param path - Array of hex keys representing the corridor path
   * @param roomA - ID of the first room
   * @param roomB - ID of the second room
   * @param userDrawn - Whether this corridor was user-drawn (defaults to false)
   * @returns The newly created corridor
   */
  addCorridor(
    path: string[],
    roomA: string,
    roomB: string,
    userDrawn: boolean = false
  ): Corridor {
    const id = generateId();
    const corridor: Corridor = {
      id,
      hexes: path,
      connects: [roomA, roomB],
      width: 1,
      userDrawn,
    };

    this.corridors.set(id, corridor);
    return corridor;
  }

  /**
   * Remove a corridor from the dungeon.
   * @param corridorId - The ID of the corridor to remove
   */
  removeCorridor(corridorId: string): void {
    this.corridors.delete(corridorId);
  }

  /**
   * Get all rooms in the dungeon.
   * @returns Array of all rooms
   */
  getAllRooms(): Room[] {
    return Array.from(this.rooms.values());
  }

  /**
   * Get all corridors in the dungeon.
   * @returns Array of all corridors
   */
  getAllCorridors(): Corridor[] {
    return Array.from(this.corridors.values());
  }

  /**
   * Clear all rooms and corridors from the dungeon.
   */
  clear(): void {
    this.rooms.clear();
    this.corridors.clear();
    this.hexToRoom.clear();
  }

  /**
   * Remove a hex from a room.
   * @param roomId - The ID of the room
   * @param hexKey - The hex key to remove
   * @returns true if hex was removed, false if room not found or hex not in room
   */
  removeHexFromRoom(roomId: string, hexKey: string): boolean {
    const room = this.rooms.get(roomId);
    if (!room || !room.hexes.has(hexKey)) return false;

    room.hexes.delete(hexKey);
    this.hexToRoom.delete(hexKey);
    return true;
  }

  /**
   * Extend an existing room by adding new hexes to it.
   * @param roomId - The ID of the room to extend
   * @param newHexes - Set of hex keys to add to the room
   * @returns The updated room, or undefined if room not found
   */
  extendRoom(roomId: string, newHexes: Set<string>): Room | undefined {
    const room = this.rooms.get(roomId);
    if (!room) return undefined;

    // Add new hexes to the room
    for (const hexKey of newHexes) {
      room.hexes.add(hexKey);
      this.hexToRoom.set(hexKey, roomId);
    }

    return room;
  }

  /**
   * Merge multiple rooms into one.
   * The first room in the array becomes the base, others are merged into it.
   * Corridors connecting the merged rooms are removed.
   * Corridors connecting to other rooms are updated to point to the merged room.
   * @param roomIds - Array of room IDs to merge (first one is the base)
   * @returns The merged room, or undefined if not enough rooms
   */
  mergeRooms(roomIds: string[]): Room | undefined {
    if (roomIds.length < 2) return undefined;

    const baseRoomId = roomIds[0];
    const baseRoom = this.rooms.get(baseRoomId);
    if (!baseRoom) return undefined;

    const roomsToMerge = roomIds.slice(1);
    const corridorsToRemove = new Set<string>();
    const corridorsToUpdate: Array<{ corridor: Corridor; oldRoomId: string }> = [];

    for (const roomId of roomsToMerge) {
      const room = this.rooms.get(roomId);
      if (!room) continue;

      // Add all hexes from this room to the base room
      for (const hexKey of room.hexes) {
        baseRoom.hexes.add(hexKey);
        this.hexToRoom.set(hexKey, baseRoomId);
      }

      // Merge labels if the room being merged has a meaningful label
      if (room.label && room.label !== room.id) {
        if (!baseRoom.label || baseRoom.label === baseRoom.id) {
          baseRoom.label = room.label;
        } else {
          // Both have labels - combine them
          baseRoom.label = `${baseRoom.label} + ${room.label}`;
        }
      }

      // Find corridors connected to this room
      const connections = this.getRoomConnections(roomId);
      for (const corridor of connections) {
        const otherRoomId = corridor.connects[0] === roomId
          ? corridor.connects[1]
          : corridor.connects[0];

        if (roomIds.includes(otherRoomId)) {
          // Corridor connects two rooms being merged - remove it
          corridorsToRemove.add(corridor.id);
        } else {
          // Corridor connects to an external room - update it
          corridorsToUpdate.push({ corridor, oldRoomId: roomId });
        }
      }

      // Remove the merged room
      this.rooms.delete(roomId);
    }

    // Remove internal corridors
    for (const corridorId of corridorsToRemove) {
      this.corridors.delete(corridorId);
    }

    // Update external corridors to point to base room
    for (const { corridor, oldRoomId } of corridorsToUpdate) {
      if (corridor.connects[0] === oldRoomId) {
        corridor.connects[0] = baseRoomId;
      } else {
        corridor.connects[1] = baseRoomId;
      }
    }

    return baseRoom;
  }

  /**
   * Find all rooms that overlap with a set of hexes.
   * @param hexes - Set of hex keys to check
   * @returns Map of room ID to set of overlapping hex keys
   */
  findOverlappingRooms(hexes: Set<string>): Map<string, Set<string>> {
    const overlaps = new Map<string, Set<string>>();

    for (const hexKey of hexes) {
      const room = this.getRoom(hexKey);
      if (room) {
        if (!overlaps.has(room.id)) {
          overlaps.set(room.id, new Set());
        }
        overlaps.get(room.id)!.add(hexKey);
      }
    }

    return overlaps;
  }

  /**
   * Check if new hexes are adjacent to an existing room (touching but not overlapping much).
   * @param roomId - The room to check adjacency with
   * @param newHexes - The new hexes to check
   * @param overlapHexes - Hexes that overlap with the room
   * @returns true if the new hexes are mostly adjacent (extension), false if mostly overlapping
   */
  isExtensionPattern(roomId: string, newHexes: Set<string>, overlapHexes: Set<string>): boolean {
    // If overlap is small relative to new hexes, it's likely an extension
    const nonOverlapping = new Set([...newHexes].filter(h => !overlapHexes.has(h)));
    return nonOverlapping.size > overlapHexes.size;
  }

  /**
   * Move a room by translating all its hexes by a delta.
   * @param roomId - The ID of the room to move
   * @param deltaQ - Change in q coordinate
   * @param deltaR - Change in r coordinate
   * @param excludeRoomIds - Optional set of room IDs to exclude from collision checking (for multi-room moves)
   * @returns The moved room, or undefined if room not found or move would collide
   */
  moveRoom(roomId: string, deltaQ: number, deltaR: number, excludeRoomIds?: Set<string>): Room | undefined {
    const room = this.rooms.get(roomId);
    if (!room) return undefined;

    // Calculate new hex positions
    const newHexes = new Set<string>();
    for (const hexKey of room.hexes) {
      const [q, r] = hexKey.split(",").map(Number);
      const newKey = `${q + deltaQ},${r + deltaR}`;
      newHexes.add(newKey);
    }

    // Check for collisions with other rooms (not this room or excluded rooms)
    for (const newKey of newHexes) {
      const existingRoom = this.getRoom(newKey);
      if (existingRoom && existingRoom.id !== roomId) {
        // Check if this room is in the excluded set (being moved together)
        if (excludeRoomIds && excludeRoomIds.has(existingRoom.id)) {
          continue; // Skip collision with rooms being moved together
        }
        // Would collide with another room
        return undefined;
      }
    }

    // Remove old hex mappings
    for (const hexKey of room.hexes) {
      this.hexToRoom.delete(hexKey);
    }

    // Update room with new hexes
    room.hexes = newHexes;

    // Add new hex mappings
    for (const hexKey of newHexes) {
      this.hexToRoom.set(hexKey, roomId);
    }

    return room;
  }

  /**
   * Update a corridor's path.
   * @param corridorId - The ID of the corridor to update
   * @param newPath - The new hex path for the corridor
   */
  updateCorridorPath(corridorId: string, newPath: string[]): void {
    const corridor = this.corridors.get(corridorId);
    if (corridor) {
      corridor.hexes = newPath;
    }
  }

  /**
   * Bisect a room along a line, splitting it into two rooms.
   * @param roomId - The room to split
   * @param lineStart - Start point of the bisect line (pixel coordinates)
   * @param lineEnd - End point of the bisect line (pixel coordinates)
   * @param hexToPixel - Function to convert hex to pixel coordinates
   * @returns Array of two new rooms, or undefined if split failed
   */
  bisectRoom(
    roomId: string,
    lineStart: { x: number; y: number },
    lineEnd: { x: number; y: number },
    hexToPixel: (q: number, r: number) => { x: number; y: number }
  ): [Room, Room] | undefined {
    const room = this.rooms.get(roomId);
    if (!room || room.hexes.size < 2) return undefined;

    // Calculate line equation: ax + by + c = 0
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    // Normal vector to the line
    const a = -dy;
    const b = dx;
    const c = -(a * lineStart.x + b * lineStart.y);

    // Classify each hex based on which side of the line it's on
    const sideA = new Set<string>();
    const sideB = new Set<string>();

    for (const hexKey of room.hexes) {
      const [q, r] = hexKey.split(",").map(Number);
      const center = hexToPixel(q, r);

      // Calculate signed distance to line
      const signedDist = a * center.x + b * center.y + c;

      if (signedDist >= 0) {
        sideA.add(hexKey);
      } else {
        sideB.add(hexKey);
      }
    }

    // Need at least one hex on each side
    if (sideA.size === 0 || sideB.size === 0) {
      return undefined;
    }

    // Remove the original room
    const originalLabel = room.label;
    const originalUserDrawn = room.userDrawn;
    const originalStyle = { ...room.style };

    // Get connected corridors before removing
    const connections = this.getRoomConnections(roomId);

    this.removeRoom(roomId);

    // Create two new rooms
    const roomA = this.addRoom(sideA, originalLabel ? `${originalLabel} (1)` : "", originalUserDrawn);
    roomA.style = { ...originalStyle };

    const roomB = this.addRoom(sideB, originalLabel ? `${originalLabel} (2)` : "", originalUserDrawn);
    roomB.style = { ...originalStyle };

    // Reconnect corridors to the appropriate new room
    for (const corridor of connections) {
      const otherRoomId = corridor.connects[0] === roomId
        ? corridor.connects[1]
        : corridor.connects[0];

      // Determine which new room the corridor should connect to
      // by checking which room shares hexes near the corridor
      let connectToA = false;
      let connectToB = false;

      for (const hexKey of corridor.hexes) {
        // Check neighbors of corridor hexes
        const [q, r] = hexKey.split(",").map(Number);
        const neighbors = [
          `${q+1},${r}`, `${q-1},${r}`, `${q},${r+1}`,
          `${q},${r-1}`, `${q+1},${r-1}`, `${q-1},${r+1}`
        ];
        for (const n of neighbors) {
          if (roomA.hexes.has(n)) connectToA = true;
          if (roomB.hexes.has(n)) connectToB = true;
        }
      }

      // Connect to whichever room is adjacent (or both if corridor touches both)
      if (connectToA) {
        this.addCorridor(corridor.hexes, roomA.id, otherRoomId, corridor.userDrawn);
      }
      if (connectToB && !connectToA) {
        this.addCorridor(corridor.hexes, roomB.id, otherRoomId, corridor.userDrawn);
      }
    }

    return [roomA, roomB];
  }

  /**
   * Carve a portion of a room into a new interior room.
   * @param roomId - The room to carve from
   * @param carveHexes - Set of hex keys to carve out
   * @returns The new carved room, or undefined if carve failed
   */
  carveRoom(roomId: string, carveHexes: Set<string>): Room | undefined {
    const room = this.rooms.get(roomId);
    if (!room) return undefined;

    // Find intersection of carve hexes with room hexes
    const intersection = new Set<string>();
    for (const hexKey of carveHexes) {
      if (room.hexes.has(hexKey)) {
        intersection.add(hexKey);
      }
    }

    // Need at least one hex to carve, and some remaining
    if (intersection.size === 0 || intersection.size >= room.hexes.size) {
      return undefined;
    }

    // Remove carved hexes from original room
    for (const hexKey of intersection) {
      room.hexes.delete(hexKey);
      this.hexToRoom.delete(hexKey);
    }

    // Create new interior room
    const newRoom = this.addRoom(intersection, "", room.userDrawn);
    newRoom.style = { ...room.style };

    return newRoom;
  }

  /**
   * Find all rooms that are adjacent (share a hex edge) with a given room.
   * @param roomId - The room to find neighbors for
   * @returns Set of adjacent room IDs
   */
  getAdjacentRooms(roomId: string): Set<string> {
    const room = this.rooms.get(roomId);
    if (!room) return new Set();

    const adjacent = new Set<string>();

    for (const hexKey of room.hexes) {
      const hex = keyToHex(hexKey);
      const neighbors = hexNeighbors(hex);

      for (const neighbor of neighbors) {
        const neighborKey = hexToKey(neighbor);
        const neighborRoomId = this.hexToRoom.get(neighborKey);

        if (neighborRoomId && neighborRoomId !== roomId) {
          adjacent.add(neighborRoomId);
        }
      }
    }

    return adjacent;
  }

  /**
   * Calculate the geometric center of a room in world coordinates.
   * @param roomId - The room ID
   * @param layout - The hex layout for coordinate conversion
   * @returns The center point, or null if room not found
   */
  getRoomCenter(roomId: string, layout: HexLayout): Point | null {
    const room = this.rooms.get(roomId);
    if (!room || room.hexes.size === 0) return null;

    let sumX = 0;
    let sumY = 0;
    let count = 0;

    for (const hexKey of room.hexes) {
      const hex = keyToHex(hexKey);
      const pixel = hexToPixel(hex, layout);
      sumX += pixel.x;
      sumY += pixel.y;
      count++;
    }

    return {
      x: sumX / count,
      y: sumY / count,
    };
  }

  /**
   * Build adjacency graph for all rooms.
   * @returns Map of room ID to set of adjacent room IDs
   */
  buildAdjacencyGraph(): Map<string, Set<string>> {
    const graph = new Map<string, Set<string>>();

    for (const roomId of this.rooms.keys()) {
      graph.set(roomId, this.getAdjacentRooms(roomId));
    }

    return graph;
  }

  /**
   * Assign colors to rooms using graph coloring algorithm.
   * Adjacent rooms will have different colors.
   * Isolated rooms get the default color (index 0).
   * @returns Map of room ID to color index
   */
  assignRoomColors(): Map<string, number> {
    const graph = this.buildAdjacencyGraph();
    const colors = new Map<string, number>();

    // Sort rooms by number of neighbors (most constrained first)
    const roomIds = Array.from(this.rooms.keys()).sort((a, b) => {
      const neighborsA = graph.get(a)?.size ?? 0;
      const neighborsB = graph.get(b)?.size ?? 0;
      return neighborsB - neighborsA;
    });

    for (const roomId of roomIds) {
      const neighbors = graph.get(roomId) ?? new Set();

      // If no neighbors, use default color (index 0)
      if (neighbors.size === 0) {
        colors.set(roomId, 0);
        continue;
      }

      // Find colors used by neighbors
      const usedColors = new Set<number>();
      for (const neighborId of neighbors) {
        const neighborColor = colors.get(neighborId);
        if (neighborColor !== undefined) {
          usedColors.add(neighborColor);
        }
      }

      // Find the first available color (starting from 1, since 0 is default)
      let color = 1;
      while (usedColors.has(color) && color < PASTEL_PALETTE.length) {
        color++;
      }

      // Wrap around if we've exhausted the palette
      if (color >= PASTEL_PALETTE.length) {
        color = 1;
      }

      colors.set(roomId, color);
    }

    return colors;
  }

  /**
   * Get the fill color for a room based on adjacency coloring.
   * @param roomId - The room ID
   * @param colorAssignments - Optional pre-computed color assignments
   * @returns The fill color hex string
   */
  getRoomFillColor(roomId: string, colorAssignments?: Map<string, number>): string {
    const assignments = colorAssignments ?? this.assignRoomColors();
    const colorIndex = assignments.get(roomId) ?? 0;
    return PASTEL_PALETTE[colorIndex];
  }
}
